'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    ClientHealthPlan,
    HealthPlan,
    createClientHealthPlan,
    deleteClientHealthPlan,
    getClientHealthPlans,
    getHealthPlans,
    updateClientHealthPlan,
} from '@/lib/api/medical/health-plan-api'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdHealthAndSafety,
    MdStar,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioData } from './prontuario-context'

function formatCPF(value: string): string {
    const digits = value.replace(/\D/g, '').slice(0, 11)
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
}

function unMaskValue(value: string): string {
    return value.replace(/\D/g, '')
}

interface HealthPlanFormData {
    healthPlanId: number | null
    cardNumber: string
    planType: string
    validUntil: string
    holderName: string
    holderCpf: string
    relationship: string
    isPrimary: boolean
}

const defaultFormData: HealthPlanFormData = {
    healthPlanId: null,
    cardNumber: '',
    planType: '',
    validUntil: '',
    holderName: '',
    holderCpf: '',
    relationship: '',
    isPrimary: false,
}

export default function ConveniosPage() {
    const { client, clientId } = useProntuarioData()
    const { user } = useAuth()

    const [clientHealthPlans, setClientHealthPlans] = useState<ClientHealthPlan[]>([])
    const [availablePlans, setAvailablePlans] = useState<HealthPlan[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [formData, setFormData] = useState<HealthPlanFormData>(defaultFormData)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [healthPlanToDelete, setHealthPlanToDelete] = useState<number | null>(null)

    useEffect(() => {
        if (clientId) {
            loadData()
        }
    }, [clientId])

    const loadData = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            const [plansResponse, availableResponse] = await Promise.all([
                getClientHealthPlans(parseInt(clientId, 10)),
                user?.organizationId ? getHealthPlans(user.organizationId) : Promise.resolve({ success: true, result: [] as HealthPlan[] }),
            ])

            if (plansResponse.success) {
                setClientHealthPlans(plansResponse.result || [])
            } else {
                toast.error(plansResponse.error || 'Erro ao carregar convênios')
            }

            if (availableResponse.success) {
                setAvailablePlans(availableResponse.result || [])
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const openModal = (plan?: ClientHealthPlan) => {
        if (plan) {
            setEditingId(plan.id)
            setFormData({
                healthPlanId: plan.healthPlanId,
                cardNumber: plan.cardNumber,
                planType: plan.planType || '',
                validUntil: plan.validUntil ? plan.validUntil.split('T')[0] : '',
                holderName: plan.holderName || '',
                holderCpf: plan.holderCpf ? formatCPF(plan.holderCpf) : '',
                relationship: plan.relationship || '',
                isPrimary: plan.isPrimary,
            })
        } else {
            setEditingId(null)
            setFormData(defaultFormData)
        }
        setModalOpen(true)
    }

    const closeModal = () => {
        setModalOpen(false)
        setEditingId(null)
        setFormData(defaultFormData)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.healthPlanId) {
            toast.error('Selecione um convênio')
            return
        }

        if (!formData.cardNumber.trim()) {
            toast.error('Informe o número da carteira')
            return
        }

        if (!clientId) return

        const parsedClientId = parseInt(clientId, 10)

        setSaving(true)
        try {
            if (editingId) {
                const response = await updateClientHealthPlan(parsedClientId, editingId, {
                    healthPlanId: formData.healthPlanId!,
                    cardNumber: formData.cardNumber.trim(),
                    planType: formData.planType.trim() || undefined,
                    validUntil: formData.validUntil || undefined,
                    holderName: formData.holderName.trim() || undefined,
                    holderCpf: unMaskValue(formData.holderCpf) || undefined,
                    relationship: formData.relationship.trim() || undefined,
                    isPrimary: formData.isPrimary,
                })
                if (response.success) {
                    toast.success('Convênio atualizado com sucesso!')
                    await loadData()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao atualizar convênio')
                }
            } else {
                const response = await createClientHealthPlan(parsedClientId, {
                    healthPlanId: formData.healthPlanId!,
                    cardNumber: formData.cardNumber.trim(),
                    planType: formData.planType.trim() || undefined,
                    validUntil: formData.validUntil || undefined,
                    holderName: formData.holderName.trim() || undefined,
                    holderCpf: unMaskValue(formData.holderCpf) || undefined,
                    relationship: formData.relationship.trim() || undefined,
                    isPrimary: formData.isPrimary,
                })
                if (response.success) {
                    toast.success('Convênio cadastrado com sucesso!')
                    await loadData()
                    closeModal()
                } else {
                    toast.error(response.error || 'Erro ao cadastrar convênio')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar convênio')
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = (id: number) => {
        setHealthPlanToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!healthPlanToDelete || !clientId) return

        try {
            const response = await deleteClientHealthPlan(parseInt(clientId, 10), healthPlanToDelete)
            if (response.success) {
                toast.success('Convênio removido com sucesso!')
                await loadData()
            } else {
                toast.error(response.error || 'Erro ao remover convênio')
            }
        } catch (error) {
            toast.error('Erro ao remover convênio')
        } finally {
            setShowDeleteModal(false)
            setHealthPlanToDelete(null)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando convênios...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-base-content">Convênios</h2>
                    <p className="text-sm text-base-content/60">
                        Planos de saúde do paciente
                    </p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="btn btn-secondary gap-2"
                    disabled={availablePlans.length === 0}
                >
                    <MdAdd className="text-lg" />
                    Adicionar Convênio
                </button>
            </div>

            {availablePlans.length === 0 && (
                <div className="alert alert-warning">
                    <span>Nenhum convênio cadastrado na empresa. Cadastre convênios nas configurações para poder adicionar aos pacientes.</span>
                </div>
            )}

            {/* Health Plans List */}
            {clientHealthPlans.length === 0 ? (
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-8 text-center">
                    <MdHealthAndSafety className="text-4xl mx-auto mb-3 text-base-content/20" />
                    <p className="text-base-content/60">Nenhum convênio cadastrado</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {clientHealthPlans.map(plan => (
                        <div
                            key={plan.id}
                            className="bg-gradient-to-br from-base-100 to-base-200/30 rounded-xl border border-base-300/50 shadow-sm overflow-hidden"
                        >
                            <div className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-secondary/10 rounded-lg">
                                            <MdHealthAndSafety className="text-xl text-secondary" />
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <h3 className="font-bold text-base-content">
                                                    {plan.healthPlan?.name || 'Convenio'}
                                                </h3>
                                                {plan.isPrimary && (
                                                    <span className="badge badge-warning badge-sm gap-1">
                                                        <MdStar className="text-xs" />
                                                        Principal
                                                    </span>
                                                )}
                                            </div>
                                            {plan.planType && (
                                                <p className="text-sm text-base-content/60">{plan.planType}</p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() => openModal(plan)}
                                            className="btn btn-ghost btn-sm btn-square"
                                        >
                                            <MdEdit className="text-lg" />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(plan.id)}
                                            className="btn btn-ghost btn-sm btn-square text-error"
                                        >
                                            <MdDelete className="text-lg" />
                                        </button>
                                    </div>
                                </div>

                                <div className="space-y-2 text-sm">
                                    <div className="flex items-center justify-between">
                                        <span className="text-base-content/60">Número da Carteira:</span>
                                        <span className="font-medium">{plan.cardNumber}</span>
                                    </div>

                                    {plan.validUntil && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-base-content/60">Validade:</span>
                                            <span className={`font-medium ${new Date(plan.validUntil) < new Date() ? 'text-error' : ''
                                                }`}>
                                                {format(new Date(plan.validUntil), 'dd/MM/yyyy', { locale: ptBR })}
                                            </span>
                                        </div>
                                    )}

                                    {plan.holderName && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-base-content/60">Titular:</span>
                                            <span className="font-medium">{plan.holderName}</span>
                                        </div>
                                    )}

                                    {plan.relationship && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-base-content/60">Relação com Titular:</span>
                                            <span className="font-medium">{plan.relationship}</span>
                                        </div>
                                    )}

                                    {plan.healthPlan?.ansCode && (
                                        <div className="flex items-center justify-between">
                                            <span className="text-base-content/60">Código ANS:</span>
                                            <span className="font-medium">{plan.healthPlan.ansCode}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {modalOpen && (
                <div className="modal modal-open">
                    <div className="modal-box !max-w-xl">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-lg font-bold">
                                {editingId ? 'Editar Convênio' : 'Adicionar Convênio'}
                            </h3>
                            <button onClick={closeModal} className="btn btn-ghost btn-sm btn-square">
                                <MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Convênio <span className="text-error">*</span></span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.healthPlanId || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, healthPlanId: parseInt(e.target.value, 10) || null }))}
                                    required
                                >
                                    <option value="">Selecione um convênio</option>
                                    {availablePlans.map(plan => (
                                        <option key={plan.id} value={plan.id}>{plan.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Número da Carteira <span className="text-error">*</span></span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Número do cartão"
                                        value={formData.cardNumber}
                                        onChange={e => setFormData(prev => ({ ...prev, cardNumber: e.target.value }))}
                                        required
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Tipo de Plano</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Enfermaria, Apartamento..."
                                        value={formData.planType}
                                        onChange={e => setFormData(prev => ({ ...prev, planType: e.target.value }))}
                                    />
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Validade</span>
                                </label>
                                <input
                                    type="date"
                                    className="input input-bordered w-full"
                                    value={formData.validUntil}
                                    onChange={e => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                                />
                            </div>

                            <div className="divider">Dados do Titular (se dependente)</div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">Nome do Titular</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="Nome completo"
                                        value={formData.holderName}
                                        onChange={e => setFormData(prev => ({ ...prev, holderName: e.target.value }))}
                                    />
                                </div>

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text font-medium">CPF do Titular</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="input input-bordered w-full"
                                        placeholder="000.000.000-00"
                                        value={formData.holderCpf}
                                        onChange={e => setFormData(prev => ({ ...prev, holderCpf: formatCPF(e.target.value) }))}
                                        maxLength={14}
                                    />
                                </div>
                            </div>

                            <div className="form-control w-full">
                                <label className="label">
                                    <span className="label-text font-medium">Relação com o Titular</span>
                                </label>
                                <select
                                    className="select select-bordered w-full"
                                    value={formData.relationship}
                                    onChange={e => setFormData(prev => ({ ...prev, relationship: e.target.value }))}
                                >
                                    <option value="">Titular</option>
                                    <option value="Cônjuge">Cônjuge</option>
                                    <option value="Filho(a)">Filho(a)</option>
                                    <option value="Pai/Mãe">Pai/Mãe</option>
                                    <option value="Dependente">Dependente</option>
                                    <option value="Outro">Outro</option>
                                </select>
                            </div>

                            <div className="form-control">
                                <label className="label cursor-pointer justify-start gap-3">
                                    <input
                                        type="checkbox"
                                        className="checkbox checkbox-secondary"
                                        checked={formData.isPrimary}
                                        onChange={e => setFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
                                    />
                                    <span className="label-text font-medium">Convênio Principal</span>
                                </label>
                            </div>

                            <div className="modal-action">
                                <button type="button" onClick={closeModal} className="btn btn-ghost" disabled={saving}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn btn-secondary gap-2" disabled={saving}>
                                    {saving ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Salvando...
                                        </>
                                    ) : (
                                        <>
                                            <MdCheck className="text-lg" />
                                            Salvar
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                    <div className="modal-backdrop" onClick={closeModal}></div>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setHealthPlanToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Remover convênio"
                message="Tem certeza que deseja remover este convênio?"
                confirmButtonText="Remover"
                cancelButtonText="Cancelar"
            />
        </div>
    )
}
